import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import * as XLSX from "xlsx";
import CircularProgress from "@mui/material/CircularProgress";
import { getOneAAFF, getAAFFPropVisit } from "../../../services";
import { toast } from "react-toastify";
import { withRouter } from "../../../providers/withRouter";
import Excel from "../../../icons/excel.svg"
import { riesgosData } from "../../../components/aaff/riesgos";

const respRules = {
  3: "si", 2: "si", 5: "si", 6: "si", 7: "si", 8: "si",
  9: "no", 11: "no", 12: "no", 14: "no", 15: "no", 16: "no", 17: "no",
  18: "no", 19: "no", 20: "no", 22: "no", 23: "no", 24: "no", 27: "no",
  28: "no", 29: "no", 30: "no", 32: "no", 33: "no", 34: "no", 35: "no",
  36: "no", 37: "no", 38: "no", 39: "no", 40: "no", 41: "no",
  43: "si", 47: "si",
  48: "no", 49: "no", 50: "no", 51: "no", 53: "no", 54: "no", 56: "no",
  57: "no", 58: "no", 59: "no", 60: "no", 61: "no", 62: "no",
};

const filasPorComunidadYRiesgo = (response) => {
  const filas = [];

  const procesarHoja = (sheet, nif, nombreComunidad) => {
    (sheet?.questions || []).forEach((question) => {
      if (respRules[question.statements_id] !== question.resp) return;
      const riesgo = riesgosData.find((r) => r.key === question.statements_id);
      if (!riesgo) return;
      filas.push({
        comunidad: nombreComunidad,
        nif: nif || '',
        riesgo: (riesgo.riesgo || []).join(", "),
        origen: riesgo.origen,
        medidas: (riesgo.medidas || []).join(" / "),
      });
    });
  };

  response?.properties?.forEach((property) => {
    if (property.workcenters?.length > 0) {
      property.workcenters.forEach((workcenter) => {
        workcenter.visitSheets?.forEach((sheet) => procesarHoja(sheet, workcenter.razonSocial));
      });
    } else {
      property.visitSheets?.forEach((sheet) => procesarHoja(sheet, property.nif, property.razonSocial));
    }
  });

  return filas;
};

const ExportarComunidadesRiesgosExcel = ({ nif }) => {
  const { t } = useTranslation(["aaff", "common", "properties", "pdfs"]);
  const [isLoading, setIsLoading] = useState(false);

  const exportar = async () => {
    if (isLoading) return;
    setIsLoading(true);
    const toastId = toast.loading(t("common:excel.generatingExcel"));
    try {
      const aaff = await getOneAAFF(nif);
      const response = await getAAFFPropVisit(nif);
      const filas = filasPorComunidadYRiesgo(response);

      // Si no hay filas, no se descarga nada: solo aviso.
      if (filas.length === 0) {
        toast.update(toastId, {
          render: t("pdfs:reports.communitiesWithoutSignals.noPendingCommunities"),
          type: "info",
          isLoading: false,
          autoClose: 4000,
        });
        return;
      }

      const rows = filas.map((f) => ({
        [t("properties:excel.community")]: f.comunidad,
        [t("properties:excel.nifCommunity")]: f.nif,
        [t("pdfs:riskReport.columns.identifiedRisk")]: f.riesgo,
        [t("pdfs:riskReport.columns.origin")]: f.origen,
        [t("pdfs:riskReport.columns.proposedMeasures")]: f.medidas,
      }));

      const worksheet = XLSX.utils.json_to_sheet(rows);
      worksheet["!cols"] = [{ wpx: 220 }, { wpx: 220 }, { wpx: 160 }, { wpx: 320 }];

      // Cabeceras en negrita
      if (worksheet["!ref"]) {
        const range = XLSX.utils.decode_range(worksheet["!ref"]);
        for (let C = range.s.c; C <= range.e.c; ++C) {
          const headerCell = XLSX.utils.encode_cell({ r: range.s.r, c: C });
          if (worksheet[headerCell]) {
            worksheet[headerCell].s = { font: { bold: true } };
          }
        }
      }

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Comunidades");

      const razonSocialLimpia = (aaff?.razonSocial || "")
        .replace(/[\\/:*?"<>|]/g, "-")
        .trim();
      const fileName = `Riesgos de comunidades${
        razonSocialLimpia ? " " + razonSocialLimpia : ""
      }.xlsx`;

      XLSX.writeFile(workbook, fileName);

      toast.update(toastId, {
        render: t("common:excel.excelSuccess"),
        type: "success",
        isLoading: false,
        autoClose: 3000,
      });
    } catch (error) {
      console.log(error);
      toast.update(toastId, {
        render: t("common:excel.excelError"),
        type: "error",
        isLoading: false,
        autoClose: 5000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <button
      className="bigBtn"
      onClick={exportar}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minWidth: "40px",
        height: "40px",
        textDecoration: "none",
        margin: 0,
        cursor: isLoading ? "default" : "pointer",
      }}
    >
      {isLoading ? (
        <CircularProgress style={{ color: "#92004D", width: "40px", height: "40px", }} />
      ) : (
        <>
          <img src={Excel} style={{ margin: 0}} alt={t("aaff:details.communitiesRisks")} width={20} height={20} />
        </>
      )}
    </button>
  );
};

export default withRouter(ExportarComunidadesRiesgosExcel);
